import {
  type CreateDevicePayload,
  type DeviceModelItem,
  isWifiRequiredDeviceModel,
  type ProvisioningDeviceDetail,
} from '../types/device.types'
import { normalizeMac } from './mac'

export interface CloneFormState {
  device_model: number
  device_mac_address: string
  station_label: string
  assigned_user_id: number | ''
  primary_extension_id: number | ''
}

/**
 * Build the clone payload, copying all settings from the source device detail.
 * Per legacy behavior:
 * - Fields NOT copied: id, last_provisioned_time, last_provisioned_from, created_at, updated_at
 * - Fields OVERRIDDEN: device_mac_address, assigned_user_id, device_model (from dialog)
 * - Everything else: copied from source, then normalized for the selected model
 */
export function buildClonePayload(
  formState: CloneFormState,
  sourceDetail: ProvisioningDeviceDetail | null,
  targetModel?: DeviceModelItem,
): CreateDevicePayload {
  const transport = sourceDetail?.transport ?? 'UDP'

  const stationLabel = formState.station_label.trim() || 'Clone'
  const payload: CreateDevicePayload = {
    device_model: formState.device_model,
    device_mac_address: normalizeMac(formState.device_mac_address),
    station_label: stationLabel,
    name: stationLabel,
    has_new_config: false,
    transport,
    jitter: sourceDetail?.jitter ?? 'normal',
    allow_provision_retrieval: sourceDetail?.allow_provision_retrieval ?? true,
    sidecar_enabled: sourceDetail?.sidecar_enabled ?? false,
    nat_method: sourceDetail?.nat_method ?? false,
  }

  if (formState.assigned_user_id !== '') {
    payload.assigned_user_id = formState.assigned_user_id
  }
  if (formState.primary_extension_id !== '') {
    payload.primary_extension_id = formState.primary_extension_id
  }
  if (sourceDetail?.corporate_directory != null) {
    payload.corporate_directory = sourceDetail.corporate_directory
  }
  if (sourceDetail?.pages != null) {
    payload.pages = sourceDetail.pages
  }
  if (sourceDetail?.line_button_count != null) {
    payload.line_button_count = sourceDetail.line_button_count
  }
  if (
    sourceDetail?.source === 'mango-new' ||
    sourceDetail?.source === 'mango-refurbished' ||
    sourceDetail?.source === 'customer'
  ) {
    payload.source = sourceDetail.source
  }
  if (sourceDetail?.warranty_expires_at != null) {
    payload.warranty_expires_at = sourceDetail.warranty_expires_at
  }
  if (sourceDetail?.time_zone != null) {
    payload.time_zone = sourceDetail.time_zone
  }
  if (sourceDetail?.dst_enable !== undefined) {
    payload.dst_enable = sourceDetail.dst_enable
  }
  if (sourceDetail?.wifi_enabled !== undefined) {
    payload.wifi_enabled = sourceDetail.wifi_enabled
  }
  if (sourceDetail?.wifi_network_ssid != null) {
    payload.wifi_network_ssid = sourceDetail.wifi_network_ssid
  }
  if (sourceDetail?.wifi_security_mode != null) {
    payload.wifi_security_mode = sourceDetail.wifi_security_mode
  }
  if (sourceDetail?.wifi_password != null) {
    payload.wifi_password = sourceDetail.wifi_password
  }
  if (sourceDetail?.acoustic_shield_enabled !== undefined) {
    payload.acoustic_shield_enabled = sourceDetail.acoustic_shield_enabled
  }
  if (sourceDetail?.server_group_host != null) {
    payload.server_group_host = sourceDetail.server_group_host
  }
  if (sourceDetail?.desired_firmware_version?.trim()) {
    payload.desired_firmware_version =
      sourceDetail.desired_firmware_version.trim()
  }

  return normalizeClonePayloadForModel(payload, targetModel)
}

export function normalizeClonePayloadForModel(
  payload: CreateDevicePayload,
  targetModel?: DeviceModelItem,
): CreateDevicePayload {
  if (!targetModel) return payload

  const normalized: CreateDevicePayload = { ...payload }

  if (!targetModel.sidecar_supported) {
    normalized.sidecar_enabled = false
  }

  if (isWifiRequiredDeviceModel(targetModel)) {
    normalized.wifi_enabled = true
  } else if (!targetModel.supports_wifi) {
    normalized.wifi_enabled = false
    delete normalized.wifi_network_ssid
    delete normalized.wifi_security_mode
    delete normalized.wifi_password
  }

  if (!targetModel.supports_acoustic_shield) {
    normalized.acoustic_shield_enabled = false
  }

  // The app device-model API does not always expose NAT support yet, so only
  // disable copied NAT when the target model explicitly reports no support.
  if (targetModel.supports_nat === false) {
    normalized.nat_method = false
  }

  if (targetModel.pages != null && targetModel.pages > 0) {
    const requestedPages = Number(normalized.pages || 1) || 1
    normalized.pages = Math.min(Math.max(requestedPages, 1), targetModel.pages)
  } else {
    normalized.pages = 0
  }

  const maxLineButtons = getCloneMaxLineButtons(normalized, targetModel)
  if (normalized.line_button_count != null && maxLineButtons != null) {
    normalized.line_button_count = Math.min(
      normalized.line_button_count,
      maxLineButtons,
    )
  }

  return normalized
}

export function getCloneMaxLineButtons(
  payload: CreateDevicePayload,
  targetModel?: DeviceModelItem,
): number | undefined {
  if (!targetModel) return undefined

  if (
    targetModel.pages != null &&
    targetModel.pages > 0 &&
    targetModel.buttons_per_page != null &&
    targetModel.buttons_per_page > 0
  ) {
    const configuredPages = Number(payload.pages || 1) || 1
    return configuredPages * targetModel.buttons_per_page
  }

  return targetModel.dss_key_count
}
